import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useState } from "react";
import { useAppDispatch } from "@store/hooks";
import { createCanvas } from "@features/workspace-canvases/state/workspaceCanvasesSlice";

interface CreateCanvasModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}

export function CreateCanvasModal({
  open,
  onClose,
  workspaceId,
}: CreateCanvasModalProps) {
  const dispatch = useAppDispatch();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const [canvasName, setCanvasName] = useState("");

  const reset = () => {
    setCanvasName("");
    setError("");
  };

  const handleCreate = async () => {
    if (!canvasName.trim()) {
      setError("Please enter a canvas name");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await dispatch(
        createCanvas({ workspaceId, canvasName: canvasName.trim() })
      ).unwrap();

      onClose();
      reset();
    } catch (err: unknown) {
      console.error("Failed to create canvas:", err);
      setError(err instanceof Error ? err.message : "Failed to create canvas");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
      reset();
    }
  };

  const isValid = canvasName.trim().length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Canvas</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Canvas Name"
          value={canvasName}
          onChange={(e) => setCanvasName(e.target.value)}
          sx={{ mt: 2 }}
          required
          disabled={submitting}
          autoFocus
          inputProps={{ maxLength: 255 }}
          helperText="Enter a name for your argument canvas"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!isValid || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
